/**
 * Hero / home secondary pages → `/task/page/{segment}`.
 * `segment` is usually a slug (`school-documents`, `student-account-set-up`); numeric `pageId` still works.
 */
export function taskPagePath(pageId: string | number): string {
  return `/task/page/${encodeURIComponent(String(pageId))}`;
}

/**
 * Learning Center task list only → `/tasklist/page/{segment}`.
 * `segment` is usually a slug from `pathSegment` in `tasklist.json`; legacy numeric `pageId` still works.
 */
export function tasklistPagePath(pageId: string | number): string {
  return `/tasklist/page/${encodeURIComponent(String(pageId))}`;
}

function normalizeUrlSegment(s: string): string {
  return s.trim().replace(/^\/+|\/+$/g, '');
}

/** Lookup for Learning Center secondary URLs when context has not merged yet (cold load). */
const LEARNING_CENTER_PATH_SEGMENT_TO_PAGE_ID: Readonly<Record<string, string>> = {
  // Current slugs (`public/data/tasklist.json` + page JSON `slug`)
  'daily-routines': '600',
  'lc-role': '300',
  'ols-navigation': '500',
  'school-overview': '400',
  'tech-support': '700',
  // Legacy slug aliases (old URLs / bookmarks)
  'monitor-grades-and-progress': '600',
  'intro-to-being-a-learning-coach': '300',
  'learning-the-platform-k12-school-basics': '500',
  'what-to-know-about-your-school': '400',
  'tech-support-basics': '700',
};

/** Canonical CMS `pageId` → preferred URL segment (must match `pathSegment` in `public/data/tasklist.json`). */
export const LEARNING_CENTER_PAGE_ID_TO_URL_SEGMENT: Readonly<Record<string, string>> = {
  '600': 'daily-routines',
  '300': 'lc-role',
  '500': 'ols-navigation',
  '400': 'school-overview',
  '700': 'tech-support',
};

export type LearningCenterUrlLookup = {
  pageId?: string;
  pathSegment?: string;
};

/**
 * Map `/tasklist/page/:param` to canonical CMS `pageId` for `pages/{pageId}.json` and APIs.
 */
export function resolveLearningCenterRouteParam(
  param: string | undefined,
  tasks: LearningCenterUrlLookup[]
): string | undefined {
  const raw = normalizeUrlSegment(param ?? '');
  if (!raw) return undefined;
  for (const t of tasks) {
    const seg = normalizeUrlSegment(t.pathSegment ?? '');
    if (seg && seg === raw) {
      const pid = (t.pageId ?? '').trim();
      return pid || undefined;
    }
  }
  for (const t of tasks) {
    const pid = (t.pageId ?? '').trim();
    if (pid && pid === raw) return pid;
  }
  const fromSeed = LEARNING_CENTER_PATH_SEGMENT_TO_PAGE_ID[raw];
  if (fromSeed) return fromSeed;
  return raw;
}

/**
 * Build `/tasklist/page/...` using slug when configured (for completion modal, deep links).
 */
export function tasklistLearningCenterPagePath(
  canonicalPageId: string | number | undefined,
  tasks: LearningCenterUrlLookup[]
): string {
  const pid = String(canonicalPageId ?? '').trim();
  if (!pid) return '/tasklist/page/';
  const row = tasks.find((t) => (t.pageId ?? '').trim() === pid);
  const segFromTask = normalizeUrlSegment(row?.pathSegment ?? '');
  if (segFromTask && /^[a-zA-Z0-9_-]+$/.test(segFromTask)) {
    return tasklistPagePath(segFromTask);
  }
  const seedSeg = LEARNING_CENTER_PAGE_ID_TO_URL_SEGMENT[pid];
  if (seedSeg && /^[a-zA-Z0-9_-]+$/.test(seedSeg)) {
    return tasklistPagePath(seedSeg);
  }
  return tasklistPagePath(pid);
}

/** Navigate from a merged Learning Center row (prefers `pathSegment` when set). */
export function tasklistLearningCenterPagePathFromTask(task: LearningCenterUrlLookup): string {
  const seg = normalizeUrlSegment(task.pathSegment ?? '');
  if (seg && /^[a-zA-Z0-9_-]+$/.test(seg)) return tasklistPagePath(seg);
  const pid = normalizeUrlSegment(task.pageId ?? '');
  if (pid && /^[a-zA-Z0-9_-]+$/.test(pid)) return tasklistPagePath(pid);
  return '/tasklist/page/';
}

/**
 * Slug segment for legacy `/tasklist/:slug` hero prep routes (matches TaskCard slugify).
 */
export function heroChecklistNameToSlugSegment(checklistName: string): string {
  return checklistName
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/'/g, '');
}

/** Maps CMS `checklistName` keys to URL path segments (JSON field name unchanged). */
export const TASKLIST_CHECKLIST_NAME_TO_SEGMENT: Record<string, string> = {
  SCHOOL_DOCUMENTS: 'school-documents',
  STUDENT_ACCOUNT_SET_UP: 'student-account-set-up',
  LEARNING_MATERIALS: 'learning-materials',
};

export type TasklistNavTask = {
  checklistName: string;
  pathSegment?: string;
};

export function getTasklistNavigateSegment(task: TasklistNavTask): string {
  const fromJson = task.pathSegment?.trim();
  if (fromJson) {
    return fromJson.replace(/^\/+|\/+$/g, '');
  }
  const mapped = TASKLIST_CHECKLIST_NAME_TO_SEGMENT[task.checklistName];
  if (mapped) {
    return mapped;
  }
  return heroChecklistNameToSlugSegment(task.checklistName || '');
}

/** Hero secondary `/task/page/{slug}` — must match `pathSegment` / `getTasklistNavigateSegment` in `tasklist.json`. */
export const HERO_PAGE_ID_TO_URL_SEGMENT: Readonly<Record<string, string>> = {
  '200': 'school-documents',
  '100': 'student-account-set-up',
};

const HERO_PATH_SEGMENT_TO_PAGE_ID: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(HERO_PAGE_ID_TO_URL_SEGMENT).map(([pageId, seg]) => [seg, pageId])
  ) as Record<string, string>
);

export type HeroTaskPageUrlLookup = {
  pageId?: string;
  pathSegment?: string;
  checklistName?: string;
};

export function heroTaskUrlSegment(row: HeroTaskPageUrlLookup): string {
  const explicit = normalizeUrlSegment(row.pathSegment ?? '');
  if (explicit && /^[a-zA-Z0-9_-]+$/.test(explicit)) return explicit;
  if (row.checklistName) {
    return getTasklistNavigateSegment({
      checklistName: row.checklistName,
      pathSegment: row.pathSegment,
    });
  }
  return normalizeUrlSegment(row.pageId ?? '');
}

/**
 * Map `/task/page/:param` to canonical CMS `pageId` for hero JSON pages (`100` / `200`).
 */
export function resolveHeroTaskPageRouteParam(
  param: string | undefined,
  rows: HeroTaskPageUrlLookup[]
): string | undefined {
  const raw = normalizeUrlSegment(param ?? '');
  if (!raw) return undefined;
  for (const r of rows) {
    const seg = heroTaskUrlSegment(r);
    if (seg && seg === raw) {
      const pid = (r.pageId ?? '').trim();
      if (pid) return pid;
    }
  }
  for (const r of rows) {
    const pid = (r.pageId ?? '').trim();
    if (pid && pid === raw) return pid;
  }
  const fromSeed = HERO_PATH_SEGMENT_TO_PAGE_ID[raw];
  if (fromSeed) return fromSeed;
  return raw;
}

export function taskHeroPagePath(
  canonicalPageId: string | number | undefined,
  rows: HeroTaskPageUrlLookup[]
): string {
  const pid = String(canonicalPageId ?? '').trim();
  if (!pid) return '/task/page/';
  const row = rows.find((r) => (r.pageId ?? '').trim() === pid);
  const seg = row ? heroTaskUrlSegment(row) : '';
  if (seg && /^[a-zA-Z0-9_-]+$/.test(seg)) {
    return taskPagePath(seg);
  }
  const seedSeg = HERO_PAGE_ID_TO_URL_SEGMENT[pid];
  if (seedSeg && /^[a-zA-Z0-9_-]+$/.test(seedSeg)) {
    return taskPagePath(seedSeg);
  }
  return taskPagePath(pid);
}

export function taskHeroPagePathFromTask(task: HeroTaskPageUrlLookup): string {
  const seg = heroTaskUrlSegment(task);
  if (seg && /^[a-zA-Z0-9_-]+$/.test(seg)) return taskPagePath(seg);
  const pid = normalizeUrlSegment(task.pageId ?? '');
  if (pid && /^[a-zA-Z0-9_-]+$/.test(pid)) return taskPagePath(pid);
  return '/task/page/';
}

export const HERO_TASK_PAGE_REDIRECT_LOOKUP: HeroTaskPageUrlLookup[] = [
  { pageId: '100', pathSegment: 'student-account-set-up', checklistName: 'STUDENT_ACCOUNT_SET_UP' },
  { pageId: '200', pathSegment: 'school-documents', checklistName: 'SCHOOL_DOCUMENTS' },
];

/** Legacy: hero task row `id` values from `tasklist.json` (`group: "tasks"`) — checklist row ids, not always `pageId`. */
export const TASKLIST_HERO_IDS_WITH_NUMERIC_ROUTE = new Set<number>([1, 2, 3]);

// --- Sidebar primary nav (`Home` vs `Learning Center` active styling) ----------------------------

export type SidebarPrimaryNavKey = 'home' | 'learning-center';

const KNOWN_SIDEBAR_HERO_PAGE_IDS = new Set(Object.keys(HERO_PAGE_ID_TO_URL_SEGMENT));

const KNOWN_SIDEBAR_LC_PAGE_IDS = new Set(Object.keys(LEARNING_CENTER_PAGE_ID_TO_URL_SEGMENT));

/** Legacy `/tasklist/{slug}` routes for hero/onboarding tasks (matches `Routes.tsx`). */
const HERO_LEGACY_SIDEBAR_TASKLIST_PREFIXES = ['/tasklist/setup-and-access', '/tasklist/learning-materials'];

function normalizeSidebarPathname(pathname: string): string {
  const beforeQuery = pathname.split(/[?#]/)[0] ?? pathname;
  const trimmed = beforeQuery.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

function canonicalPageIdsFromLookupRows(rows: { pageId?: string }[]): Set<string> {
  const ids = new Set<string>();
  for (const r of rows) {
    const pid = (r.pageId ?? '').trim();
    if (pid) ids.add(pid);
  }
  return ids;
}

export function resolveSidebarPrimaryNavKey(
  pathname: string,
  heroTasksForLookup: HeroTaskPageUrlLookup[],
  learningCenterTasksForLookup: LearningCenterUrlLookup[]
): SidebarPrimaryNavKey | null {
  const path = normalizeSidebarPathname(pathname);
  const lcLookup = learningCenterTasksForLookup;
  const heroSeenPageIds = canonicalPageIdsFromLookupRows(heroTasksForLookup);
  const lcPid = canonicalPageIdsFromLookupRows(lcLookup);

  if (path === '/overview' || path === '/tasklist') {
    return 'home';
  }

  if (path === '/learning-center' || path.startsWith('/learning-center/')) {
    return 'learning-center';
  }

  const effectiveHeroCanonicalIds = new Set<string>([
    ...KNOWN_SIDEBAR_HERO_PAGE_IDS,
    ...heroSeenPageIds,
  ]);

  const taskPageMatch = path.match(/^\/task\/page\/([^/]+)$/);
  if (taskPageMatch) {
    const param = taskPageMatch[1];
    const canonHero = resolveHeroTaskPageRouteParam(param, heroTasksForLookup);
    if (canonHero && effectiveHeroCanonicalIds.has(canonHero)) {
      return 'home';
    }
  }

  const tasklistPageMatch = path.match(/^\/tasklist\/page\/([^/]+)$/);
  if (tasklistPageMatch) {
    const param = tasklistPageMatch[1];
    const canonHero = resolveHeroTaskPageRouteParam(param, heroTasksForLookup);
    if (canonHero && effectiveHeroCanonicalIds.has(canonHero)) {
      return 'home';
    }

    const canonLc = resolveLearningCenterRouteParam(param, lcLookup);
    const lcResolved =
      canonLc &&
      ((lcPid.size > 0 && lcPid.has(canonLc)) ||
        (lcPid.size === 0 && KNOWN_SIDEBAR_LC_PAGE_IDS.has(canonLc)));
    if (lcResolved) {
      return 'learning-center';
    }
  }

  if (
    path.startsWith('/tasklist/') &&
    path !== '/tasklist' &&
    !path.startsWith('/tasklist/page/')
  ) {
    for (const row of heroTasksForLookup) {
      const seg = getTasklistNavigateSegment({
        checklistName: row.checklistName || '',
        pathSegment: row.pathSegment,
      });
      const base = `/tasklist/${seg}`;
      if (path === base || path.startsWith(`${base}/`)) {
        return 'home';
      }
    }
    if (
      HERO_LEGACY_SIDEBAR_TASKLIST_PREFIXES.some(
        (p) => path === p || path.startsWith(`${p}/`)
      )
    ) {
      return 'home';
    }
  }

  return null;
}
